import React, { useCallback, useMemo, useState } from "react";
import { formatISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    RevertHistoryPreviewBody,
    useRevertHistoryMutation,
    useRevertHistoryPreviewQuery,
} from "@api/auditLog.api";
import { invalidateOrganizationHistory } from "@api/organizationAuditLog.api";
import {
    ActivityProcessName,
    APIProcessName,
    SQSProcessName,
} from "@common/constants/process-names";
import { invalidateAccrualMatchCaches } from "@components/accounting/accrualReview/accrualReviewCache";
import { isAccrualEffectiveCountSource } from "../utils/accrualApproveSourceTypes";
import { HistoryRow, REVERT_TOAST_ID } from "../views/historyViewConfig";
import { RevertProgressToast } from "../components/RevertProgressToast";
import { useHistoryDetailsListParams } from "../details/useHistoryDetailsListParams";

type RevertableSourceType =
    | SQSProcessName.TRANSACTION_RULE_APPLICATION
    | APIProcessName.TRANSACTION_BULK_UPDATE
    | APIProcessName.ACCRUAL_REVIEW_APPROVE
    | APIProcessName.ACCRUAL_REVIEW_BULK_APPROVE
    | APIProcessName.ACCRUAL_MATCH_CREATE
    | ActivityProcessName.TRANSACTION_ENRICHMENT;

const REVERTABLE_SOURCE_TYPES: ReadonlySet<string> = new Set<string>([
    SQSProcessName.TRANSACTION_RULE_APPLICATION,
    APIProcessName.TRANSACTION_BULK_UPDATE,
    APIProcessName.ACCRUAL_REVIEW_APPROVE,
    APIProcessName.ACCRUAL_REVIEW_BULK_APPROVE,
    APIProcessName.ACCRUAL_MATCH_CREATE,
    ActivityProcessName.TRANSACTION_ENRICHMENT,
]);

const PROGRESS_LABEL = "Updating transactions...";

export function isRevertableSourceType(
    sourceType: string | undefined | null,
): sourceType is RevertableSourceType {
    return sourceType != null && REVERTABLE_SOURCE_TYPES.has(sourceType);
}

export interface RevertSourceTarget {
    sourceId?: string;
    count: number;
    resourceIds?: string[];
    system?: string;
}

export interface PreviewField {
    field: string;
    transactionCount: number;
}

interface UseHistorySourceRevertResult<TTarget extends RevertSourceTarget> {
    revertTarget: TTarget | null;
    requestRevert: (target: TTarget) => void;
    cancelRevert: () => void;
    confirmRevert: () => Promise<boolean>;
    previewFields: PreviewField[] | undefined;
    previewCount: number | undefined;
    isPreviewLoading: boolean;
}

export function useHistorySourceRevert<TTarget extends RevertSourceTarget>(
    historyRow: HistoryRow | null,
): UseHistorySourceRevertResult<TTarget> {
    const queryClient = useQueryClient();
    const revertMutation = useRevertHistoryMutation();
    const toaster = useToaster();

    const [revertTarget, setRevertTarget] = useState<TTarget | null>(null);
    // Revert undoes the whole source, so it ignores the list's outcome filter.
    const { channel, channels } = useHistoryDetailsListParams(historyRow);

    const previewBody = useMemo<RevertHistoryPreviewBody | null>(() => {
        if (!historyRow || !revertTarget) {
            return null;
        }
        const fields = historyRow.fields ?? [];
        if (fields.length === 0) {
            return null;
        }
        if (!isRevertableSourceType(historyRow.sourceType)) {
            return null;
        }
        return {
            channel,
            channels,
            date: formatISO(historyRow.date),
            sourceType: historyRow.sourceType,
            resourceType: historyRow.resourceType,
            actorId: historyRow.actorId ?? null,
            fields,
            changeTypes: historyRow.changeTypes ?? [],
            sourceId: revertTarget.sourceId,
            resourceIds:
                revertTarget.resourceIds && revertTarget.resourceIds.length > 0
                    ? revertTarget.resourceIds
                    : undefined,
            system: revertTarget.system,
        };
    }, [historyRow, revertTarget, channel, channels]);

    const previewQuery = useRevertHistoryPreviewQuery({
        body: previewBody,
        enabled: !!revertTarget,
    });

    const requestRevert = useCallback((target: TTarget) => {
        setRevertTarget(target);
    }, []);

    const cancelRevert = useCallback(() => {
        setRevertTarget(null);
    }, []);

    const confirmRevert = useCallback(async (): Promise<boolean> => {
        if (!historyRow || !revertTarget) {
            return false;
        }

        const fields = historyRow.fields ?? [];
        if (fields.length === 0) {
            toaster.error(
                "Can't revert: this activity row is missing field metadata.",
            );
            setRevertTarget(null);
            return false;
        }

        if (!isRevertableSourceType(historyRow.sourceType)) {
            toaster.error(
                "Can't revert: this activity type does not support revert.",
            );
            setRevertTarget(null);
            return false;
        }

        try {
            const result = await revertMutation.mutateAsync({
                channel,
                channels,
                date: formatISO(historyRow.date),
                sourceType: historyRow.sourceType,
                resourceType: historyRow.resourceType,
                actorId: historyRow.actorId ?? null,
                fields,
                changeTypes: historyRow.changeTypes ?? [],
                sourceId: revertTarget.sourceId,
                resourceIds:
                    revertTarget.resourceIds &&
                    revertTarget.resourceIds.length > 0
                        ? revertTarget.resourceIds
                        : undefined,
                system: revertTarget.system,
            });

            if (result && "jobId" in result) {
                toaster.toast(
                    <RevertProgressToast
                        label={PROGRESS_LABEL}
                        processed={0}
                        total={revertTarget.count}
                    />,
                    REVERT_TOAST_ID,
                    { preventAutoClose: true },
                );
            } else {
                await queryClient.refetchQueries({ queryKey: ["audit-log"] });
                invalidateOrganizationHistory(queryClient);
                queryClient.invalidateQueries({ queryKey: ["transactions"] });
                if (isAccrualEffectiveCountSource(historyRow.sourceType)) {
                    invalidateAccrualMatchCaches(queryClient);
                }
                toaster.success("Revert completed");
            }
            setRevertTarget(null);
            return true;
        } catch {
            toaster.error("Failed to revert changes. Please try again.");
            setRevertTarget(null);
            return false;
        }
    }, [
        historyRow,
        revertTarget,
        channel,
        channels,
        revertMutation,
        queryClient,
        toaster,
    ]);

    return {
        revertTarget,
        requestRevert,
        cancelRevert,
        confirmRevert,
        previewFields: previewQuery.data?.fields,
        previewCount: previewQuery.data?.transactionCount,
        isPreviewLoading: previewQuery.isLoading,
    };
}
